import { User } from '@auth0/auth0-react'
import { bind, SUSPENSE } from '@react-rxjs/core'
import { createSignal } from '@react-rxjs/utils'
import { combineLatest, Observable, of } from 'rxjs'
import { defaultIfEmpty, filter, switchMap } from 'rxjs/operators'

import { Result, right } from '../../utils/result'
import { mapSuspended } from '../../utils/rx/operators'
import { userClaimIds } from '../../utils/userClaims'
import { latestAuthState$ } from '../auth/state'
import { dispensaryByCodeOrEmpty } from './effects'
import { DispensaryData } from './index'

export type CurrentDispensaryWithPatientFallbackStatus =
  | 'currentDispensaryAnonymous'
  | 'currentDispensary'
  | 'patientDispensary'
  | 'rootSiteAnonymous'

const [currentDispensaryWithPatientFallbackInput$, setCurrentDispensaryWithPatientFallbackInput] =
  createSignal<{
    dispensaryContext: DispensaryData | null
  }>()

export { setCurrentDispensaryWithPatientFallbackInput }

const patientDispensary$ = (
  user: User | undefined,
): Observable<SUSPENSE | Result<DispensaryData | null, Error>> =>
  user?.[userClaimIds.dispensaryPractitionerCode]
    ? dispensaryByCodeOrEmpty(user[userClaimIds.dispensaryPractitionerCode]).pipe(
        mapSuspended((dispensary) => (dispensary ? right(dispensary) : right(null))),
        defaultIfEmpty(right(null)),
      )
    : of(right(null))

const currentDispensaryWithPatientFallback$: Observable<
  | SUSPENSE
  | readonly [CurrentDispensaryWithPatientFallbackStatus, Result<DispensaryData | null, Error>]
> = currentDispensaryWithPatientFallbackInput$.pipe(
  switchMap(({ dispensaryContext }) =>
    latestAuthState$.pipe(
      filter(({ isLoading }) => !isLoading),
      switchMap(({ isAuthenticated, user }) =>
        dispensaryContext
          ? combineLatest([
              of(
                isAuthenticated
                  ? ('currentDispensary' as const)
                  : ('currentDispensaryAnonymous' as const),
              ),
              of(right(dispensaryContext)),
            ] as const)
          : isAuthenticated
            ? patientDispensary$(user).pipe(
                mapSuspended(
                  (dispensaryResult) => ['patientDispensary' as const, dispensaryResult] as const,
                ),
              )
            : of(['rootSiteAnonymous' as const, right(null)] as const),
      ),
    ),
  ),
)

export const [
  useCurrentDispensaryWithPatientFallback,
  latestCurrentDispensaryWithPatientFallback$,
] = bind(currentDispensaryWithPatientFallback$)
